import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import hepatoRecordsApi, {
  type EntitySpec,
  type FieldSpec,
  type HepatoRecord,
} from '../../api/hepatoRecordsApi';

const { Text } = Typography;

/** Încarcă specificația unei entități (formularele sunt generate din ea). */
export const useEntitySpec = (entity: string) => {
  const [spec, setSpec] = useState<EntitySpec | null>(null);
  useEffect(() => {
    let cancelled = false;
    hepatoRecordsApi
      .schema()
      .then((s) => {
        if (!cancelled) setSpec(s.entities.find((e) => e.entity === entity) ?? null);
      })
      .catch(() => undefined); // eroarea e afișată de interceptorul axios
    return () => {
      cancelled = true;
    };
  }, [entity]);
  return spec;
};

const toInputValue = (f: FieldSpec, v: unknown) => {
  if (v === null || v === undefined) return f.type === 'boolean' ? false : undefined;
  if (f.type === 'date') return String(v).slice(0, 10);
  return v;
};

const displayValue = (f: FieldSpec, v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (f.type === 'boolean') return v ? 'Da' : 'Nu';
  if (f.type === 'date') return new Date(String(v)).toLocaleDateString('ro-RO');
  if (f.type === 'select') return f.options?.find((o) => o.value === v)?.label ?? String(v);
  if (f.type === 'multiselect') {
    return (v as string[]).map((x) => f.options?.find((o) => o.value === x)?.label ?? x).join(', ') || '—';
  }
  return f.unit ? `${v} ${f.unit}` : String(v);
};

interface FieldInputProps {
  field: FieldSpec;
  disabled?: boolean;
  // Form.Item injectează value / onChange / id / checked pe copilul direct — le transmitem mai departe.
  [prop: string]: unknown;
}

const FieldInput = ({ field, disabled, ...rest }: FieldInputProps) => {
  switch (field.type) {
    case 'textarea':
      return <Input.TextArea {...rest} rows={3} maxLength={field.maxLength} disabled={disabled} />;
    case 'number':
      return <InputNumber {...rest} style={{ width: '100%' }} min={field.min} max={field.max} addonAfter={field.unit} disabled={disabled} />;
    case 'date':
      return <Input {...rest} type="date" disabled={disabled} />;
    case 'select':
      return <Select {...rest} allowClear options={field.options} disabled={disabled} placeholder="Selectează" />;
    case 'multiselect':
      return <Select {...rest} mode="multiple" allowClear options={field.options} disabled={disabled} placeholder="Selectează" />;
    case 'boolean':
      return <Checkbox {...rest} disabled={disabled}>Da</Checkbox>;
    default:
      return <Input {...rest} maxLength={field.maxLength} disabled={disabled} />;
  }
};

/** Formular generat din specificație (cu showIf, secțiuni, reguli de obligativitate). */
export const EntityFormFields = ({ spec }: { spec: EntitySpec }) => {
  const form = Form.useFormInstance();
  const byKey = useMemo(() => new Map(spec.fields.map((f) => [f.key, f])), [spec]);

  const renderField = (f: FieldSpec) => (
    <Form.Item noStyle shouldUpdate key={f.key}>
      {() => {
        if (f.showIf) {
          const dep = form.getFieldValue(f.showIf.field);
          const visible = f.showIf.truthy !== undefined ? Boolean(dep) === f.showIf.truthy : (f.showIf.in ?? []).includes(String(dep));
          if (!visible) return null;
        }
        return (
          <Col xs={24} md={f.type === 'textarea' ? 24 : 12}>
            <Form.Item
              name={f.key}
              label={
                <span>
                  {f.label}
                  {f.pendingReview && (
                    <Tag style={{ marginLeft: 6 }} color="gold">
                      de validat
                    </Tag>
                  )}
                </span>
              }
              extra={f.help}
              valuePropName={f.type === 'boolean' ? 'checked' : 'value'}
              rules={f.required ? [{ required: true, message: `${f.label} este obligatoriu` }] : undefined}
            >
              <FieldInput field={f} />
            </Form.Item>
          </Col>
        );
      }}
    </Form.Item>
  );

  const sections = spec.sections ?? [{ title: '', fields: spec.fields.map((f) => f.key) }];
  return (
    <>
      {sections.map((s) => (
        <div key={s.title || 'all'}>
          {s.title && <Divider orientation="left" style={{ marginTop: 8 }}>{s.title}</Divider>}
          <Row gutter={16}>{s.fields.map((k) => byKey.get(k)).filter((f): f is FieldSpec => Boolean(f)).map(renderField)}</Row>
        </div>
      ))}
    </>
  );
};

const initialValues = (spec: EntitySpec, record?: HepatoRecord | null) => {
  const out: Record<string, unknown> = {};
  for (const f of spec.fields) {
    out[f.key] = record ? toInputValue(f, record[f.key]) : f.defaultValue ?? (f.type === 'boolean' ? false : undefined);
  }
  return out;
};

const applyServerErrors = (form: ReturnType<typeof Form.useForm>[0], e: any) => {
  const errors = e?.response?.data?.errors as Array<{ field: string; message: string }> | undefined;
  if (errors?.length) {
    form.setFields(errors.map((er) => ({ name: er.field, errors: [er.message] })));
    return true;
  }
  return false;
};

interface EntityStepProps {
  formId: string;
  entity: string;
  /** Apelat după orice modificare (reîmprospătează starea fluxului / progresul). */
  onChanged?: () => void;
  /** Conținut afișat deasupra tabelului / formularului (ex. sugestie screening). */
  header?: React.ReactNode;
  title?: string;
}

/** Pas generic: tabel 1:N cu adăugare/editare/ștergere, sau formular unic (cardinality one). */
const EntityStep = ({ formId, entity, onChanged, header, title }: EntityStepProps) => {
  const spec = useEntitySpec(entity);
  const [records, setRecords] = useState<HepatoRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<HepatoRecord | 'new' | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRecords(await hepatoRecordsApi.list(formId, entity));
    } finally {
      setLoading(false);
    }
  }, [formId, entity]);

  useEffect(() => {
    void load();
  }, [load]);

  const single = spec?.cardinality === 'one';
  const current = single ? records[0] ?? null : editing && editing !== 'new' ? editing : null;

  useEffect(() => {
    if (spec && (single || editing)) form.setFieldsValue(initialValues(spec, current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec, single, editing, records]);

  if (!spec) return <Card loading title={title ?? entity} />;

  const save = async () => {
    let values: Record<string, unknown>;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      if (current) await hepatoRecordsApi.update(formId, entity, current.id, values);
      else await hepatoRecordsApi.create(formId, entity, values);
      message.success('Salvat');
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) {
      applyServerErrors(form, e); // alte erori: interceptorul axios
    } finally {
      setSaving(false);
    }
  };

  const remove = async (record: HepatoRecord) => {
    await hepatoRecordsApi.remove(formId, entity, record.id);
    message.success('Șters');
    await load();
    onChanged?.();
  };

  const fieldByKey = new Map(spec.fields.map((f) => [f.key, f]));

  if (single) {
    return (
      <Card title={title ?? spec.title} loading={loading} data-testid={`entity-${entity}`}>
        {header}
        <Form form={form} layout="vertical" initialValues={initialValues(spec, current)}>
          <EntityFormFields spec={spec} />
        </Form>
        <Space>
          <Button type="primary" loading={saving} onClick={() => void save()} data-testid="entity-save">
            {current ? 'Actualizează' : 'Salvează'}
          </Button>
          {current && (
            <Popconfirm title="Ștergi înregistrarea?" okText="Șterge" cancelText="Anulează" onConfirm={() => void remove(current)}>
              <Button danger icon={<DeleteOutlined />}>Șterge</Button>
            </Popconfirm>
          )}
        </Space>
      </Card>
    );
  }

  return (
    <Card
      title={title ?? spec.title}
      data-testid={`entity-${entity}`}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing('new')} data-testid="entity-add">
          Adaugă
        </Button>
      }
    >
      {header}
      <Table<HepatoRecord>
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={records}
        pagination={false}
        locale={{ emptyText: 'Nicio înregistrare.' }}
        columns={[
          ...spec.listFields.map((k) => {
            const f = fieldByKey.get(k)!;
            return { title: f.label, dataIndex: k, render: (v: unknown) => displayValue(f, v) };
          }),
          {
            title: '',
            width: 90,
            render: (_: unknown, r: HepatoRecord) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} aria-label="Editează" onClick={() => setEditing(r)} />
                <Popconfirm title="Ștergi înregistrarea?" okText="Șterge" cancelText="Anulează" onConfirm={() => void remove(r)}>
                  <Button size="small" danger icon={<DeleteOutlined />} aria-label="Șterge" />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        open={editing !== null}
        title={editing === 'new' ? `Adaugă — ${spec.title}` : `Editează — ${spec.title}`}
        width={760}
        okText="Salvează"
        cancelText="Anulează"
        confirmLoading={saving}
        onOk={() => void save()}
        onCancel={() => setEditing(null)}
        forceRender
      >
        <Form form={form} layout="vertical" initialValues={initialValues(spec, current)}>
          <EntityFormFields spec={spec} />
        </Form>
        <Text type="secondary">Câmpurile marcate „de validat” sunt propuneri în curs de validare medicală.</Text>
      </Modal>
    </Card>
  );
};

export default EntityStep;
